import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    AreaChart, Area
} from 'recharts';

const AdminRevenue = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchStats = async () => {
        try {
            setLoading(true);
            const response = await api.get('/admin/stats');
            setStats(response.data);
        } catch (error) {
            toast.error('Không thể tải thống kê doanh thu');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStats();
    }, []);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('vi-VN').format(amount) + '₫';
    };

    if (loading) return <div className="p-8 text-center font-bold">Đang tính toán số liệu tài chính...</div>;

    const summaryCards = [
        { label: 'Tổng doanh thu', value: stats?.totalRevenue || 0, color: 'text-blue-600', icon: 'payments', desc: 'Dựa trên các đơn đã hoàn thành/xử lý' },
        { label: 'Tổng chi phí', value: stats?.totalCost || 0, color: 'text-red-600', icon: 'shopping_bag', desc: 'Giá vốn của các sản phẩm đã bán' },
        { label: 'Tổng lợi nhuận', value: stats?.totalProfit || 0, color: 'text-green-600', icon: 'trending_up', desc: 'Doanh thu - Chi phí' },
        { label: 'Tỷ suất lợi nhuận', value: stats?.totalRevenue > 0 ? ((stats.totalProfit / stats.totalRevenue) * 100).toFixed(1) + '%' : '0%', color: 'text-purple-600', icon: 'analytics', desc: 'Hiệu quả kinh doanh' }
    ];

    // Mock data for chart as current backend only returns totals. 
    // In future, backend could return data monthly/daily for the chart.
    const chartData = [
        { name: 'Thống kê tổng quát', DoanhThu: stats?.totalRevenue || 0, ChiPhi: stats?.totalCost || 0, LoiNhuan: stats?.totalProfit || 0 }
    ];

    return (
        <div className="p-3 sm:p-8 bg-gray-50 min-h-full transition-all duration-300">
            <div className="mb-6 sm:mb-8">
                <h1 className="text-xl sm:text-3xl font-black text-slate-800 uppercase tracking-tight">Thống kê Tài chính</h1>
                <p className="text-[10px] sm:text-sm text-slate-500 font-bold uppercase tracking-widest mt-1">Báo cáo doanh thu và hiệu quả kinh nhuận</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8 sm:mb-10">
                {summaryCards.map((card, idx) => (
                    <div key={idx} className="bg-white p-4 sm:p-6 rounded-[24px] sm:rounded-[32px] shadow-sm border border-gray-100 flex items-center sm:items-start gap-3 sm:gap-4 hover:shadow-md transition-shadow">
                        <div className={`p-2 sm:p-3 rounded-xl sm:rounded-2xl bg-gray-50 ${card.color}`}>
                            <span className="material-symbols-outlined text-[24px] sm:text-[28px]">{card.icon}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-[9px] sm:text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5 sm:mb-1">{card.label}</p>
                            <p className={`text-lg sm:text-xl font-black ${card.color}`}>{typeof card.value === 'number' ? formatCurrency(card.value) : card.value}</p>
                            <p className="text-[8px] sm:text-[9px] font-bold text-slate-400 mt-0.5 sm:mt-1 uppercase tracking-tight opacity-70">{card.desc}</p>
                        </div>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bg-white p-5 sm:p-8 rounded-[30px] sm:rounded-[40px] shadow-sm border border-gray-100">
                    <div className="flex justify-between items-center mb-6 sm:mb-8">
                        <div>
                            <h2 className="text-base sm:text-lg font-black text-slate-800 uppercase tracking-tight">Biểu đồ so sánh</h2>
                            <p className="text-[9px] sm:text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">Doanh thu vs Chi phí vs Lợi nhuận</p>
                        </div>
                    </div>
                    <div className="h-[280px] sm:h-[400px] w-full mt-2 sm:mt-4">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" opacity={0.5} />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 'bold' }} hide={window.innerWidth < 640} />
                                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 'bold' }} tickFormatter={(val) => val / 1000000 + 'M'} />
                                <Tooltip 
                                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)', padding: '10px' }}
                                    formatter={(value) => formatCurrency(value)}
                                />
                                <Legend iconType="circle" wrapperStyle={{ paddingTop: '10px', fontSize: '10px', fontWeight: 'bold' }} />
                                <Bar dataKey="DoanhThu" name="Doanh thu" fill="#3B82F6" radius={[4, 4, 0, 0]} barSize={window.innerWidth < 640 ? 40 : 60} />
                                <Bar dataKey="ChiPhi" name="Chi phí" fill="#EF4444" radius={[4, 4, 0, 0]} barSize={window.innerWidth < 640 ? 40 : 60} />
                                <Bar dataKey="LoiNhuan" name="Lợi nhuận" fill="#10B981" radius={[4, 4, 0, 0]} barSize={window.innerWidth < 640 ? 40 : 60} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="bg-slate-900 p-5 sm:p-8 rounded-[30px] sm:rounded-[40px] shadow-xl text-white">
                    <h2 className="text-base sm:text-lg font-black uppercase tracking-tight mb-4 sm:mb-6">Phân tích hiệu suất</h2>
                    <div className="space-y-6 sm:space-y-8">
                        <div>
                            <div className="flex justify-between text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">
                                <span>Doanh thu đạt được</span>
                                <span className="text-blue-400">100%</span>
                            </div>
                            <div className="w-full bg-slate-800 h-1.5 sm:h-2 rounded-full overflow-hidden">
                                <div className="bg-blue-500 h-full w-full"></div>
                            </div>
                        </div>

                        <div className="bg-slate-800/50 p-4 sm:p-6 rounded-2xl sm:rounded-3xl border border-slate-700/50 mt-4 sm:mt-8">
                            <p className="text-[9px] sm:text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 sm:mb-4 text-center sm:text-left">Lưu ý quản trị</p>
                            <ul className="space-y-3 sm:space-y-4">
                                <li className="flex gap-2 sm:gap-3 items-start">
                                    <span className="material-symbols-outlined text-green-400 text-xs sm:text-sm mt-0.5">check_circle</span>
                                    <p className="text-[10px] sm:text-[11px] font-bold text-slate-300 leading-relaxed italic">
                                        Doanh thu đã được loại trừ các đơn hàng "Đã hủy".
                                    </p>
                                </li>
                                <li className="flex gap-2 sm:gap-3 items-start">
                                    <span className="material-symbols-outlined text-blue-400 text-xs sm:text-sm mt-0.5">info</span>
                                    <p className="text-[10px] sm:text-[11px] font-bold text-slate-300 leading-relaxed italic">
                                        Chi phí tính bằng giá vốn tại thời điểm khách đặt.
                                    </p>
                                </li>
                                <li className="flex gap-2 sm:gap-3 items-start">
                                    <span className="material-symbols-outlined text-yellow-400 text-xs sm:text-sm mt-0.5">warning</span>
                                    <p className="text-[10px] sm:text-[11px] font-bold text-slate-300 leading-relaxed italic">
                                        Cần nhập đủ "Giá vốn" sản phẩm để số liệu lợi nhuận chuẩn.
                                    </p>
                                </li>
                            </ul>
                        </div>
                        
                        <button onClick={fetchStats} className="w-full bg-white text-slate-900 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-black text-[10px] sm:text-xs uppercase tracking-widest hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-2">
                            <span className="material-symbols-outlined text-[16px] sm:text-sm">refresh</span>
                            Làm mới dữ liệu
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminRevenue;
